import { useEffect, useState } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { Trash2, Plus, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ROLES = ["admin", "finance", "support", "viewer"];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // { mode: 'create'|'edit', data }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin-users");
      setRows(data);
    } catch (err) {
      toast.error("Failed to load admin users");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.mode === "create") {
        await api.post("/admin-users", form.data);
        toast.success("Admin user created");
      } else {
        const body = { ...form.data };
        if (!body.password) delete body.password;
        delete body.email;
        delete body.id;
        await api.patch(`/admin-users/${form.data.id}`, body);
        toast.success("Admin user updated");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete admin ${row.email}?`)) return;
    try {
      await api.delete(`/admin-users/${row.id}`);
      toast.success("Admin deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader
        title="Admin Users & Permissions"
        subtitle="Manage sub-admins with role-based access (admin, finance, support, viewer)"
        actions={
          <button onClick={() => setForm({ mode: "create", data: { name: "", email: "", password: "", role: "viewer" } })} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5" data-testid="admin-add-btn">
            <Plus size={14} /> Add admin user
          </button>
        }
      />

      <DataTable
        testId="admin-users-table"
        loading={loading}
        rows={rows}
        columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "role", label: "Role", render: (r) => <Badge variant={r.role === "admin" ? "success" : "neutral"}>{r.role}</Badge> },
          { key: "created_at", label: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
        ]}
        actions={(row) => (
          <div className="flex items-center gap-1 justify-end">
            <button onClick={() => setForm({ mode: "edit", data: { ...row, password: "" } })} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 rounded-sm hover:border-blue-300 hover:text-blue-700" data-testid={`admin-edit-${row.id}`}>
              <Pencil size={12} /> Edit
            </button>
            {row.id !== me?.id && (
              <button onClick={() => onDelete(row)} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200" data-testid={`admin-delete-${row.id}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
      />

      {form && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="font-display font-bold">{form.mode === "create" ? "Create admin user" : "Edit admin user"}</h3>
              <button onClick={() => setForm(null)} className="h-8 w-8 flex items-center justify-center"><X size={16} /></button>
            </div>
            <form onSubmit={onSubmit} className="p-5 space-y-3">
              <Field label="Name" value={form.data.name} onChange={(v) => setForm({ ...form, data: { ...form.data, name: v } })} testId="admin-name" required />
              {form.mode === "create" && (
                <Field label="Email" type="email" value={form.data.email} onChange={(v) => setForm({ ...form, data: { ...form.data, email: v } })} testId="admin-email" required />
              )}
              <Field
                label={form.mode === "create" ? "Password" : "New password (leave blank to keep)"}
                type="password"
                value={form.data.password}
                onChange={(v) => setForm({ ...form, data: { ...form.data, password: v } })}
                testId="admin-password"
                required={form.mode === "create"}
              />
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Role</label>
                <select value={form.data.role} onChange={(e) => setForm({ ...form, data: { ...form.data, role: e.target.value } })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="admin-role">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <div className="text-[11px] text-gray-500 mt-1">
                  <b>admin</b>: full access · <b>finance</b>: deposits/withdraws · <b>support</b>: tickets · <b>viewer</b>: read-only
                </div>
              </div>
              <button type="submit" className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm" data-testid="admin-submit">
                {form.mode === "create" ? "Create admin" : "Save changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
    </div>
  );
}
